import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import type { ComponentType } from 'react';
import {
  Calendar, BookOpen, Dumbbell, LayoutDashboard, GraduationCap, CalendarDays, BarChart3, History, MapPin, MoreHorizontal, UserRound, Trophy, Wrench } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthStore, useIndividualClassSettings } from '@sportschool/shared-logic';
import { StudentMoreMenuDialog } from './StudentMoreMenuDialog';

interface Props {
  schoolId?: string;
}

export function BottomNavBar({ schoolId }: Props) {
  const { t } = useTranslation();
  const location = useLocation();
  const userContext = useAuthStore((s) => s.userContext);
  const activeRole = useAuthStore((s) => s.activeRole);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const hasIndividualClassesFeature = !!userContext?.activeSchool?.enabledFeatures?.includes('IndividualClasses');
  const { data: individualSettings } = useIndividualClassSettings(hasIndividualClassesFeature && schoolId ? schoolId : '');

  if (!schoolId) return null;

  const isLoaded = !!(userContext && userContext.activeSchool?.id === schoolId);
  if (!isLoaded) return null;

  const enabledModules = userContext?.activeSchool?.enabledModules || [];
  const base = `/schools/${schoolId}`;

  const isStudent = activeRole !== 'Admin' && activeRole !== 'Coach';

  const items: Array<{ to: string; icon: ComponentType<{ className?: string }>; label: string }> = [];

  if (activeRole === 'Admin') {
    // Admin Navigation
    items.push({ to: `${base}/dashboard`, icon: LayoutDashboard, label: t('nav.dashboard') });
    items.push({ to: `${base}/sessions`, icon: Calendar, label: t('nav.sessions') });
    
    if (enabledModules.includes('schedules')) {
      items.push({ to: `${base}/schedules`, icon: CalendarDays, label: t('nav.schedules') });
    }
    
    if (enabledModules.includes('users')) {
      items.push({ to: `${base}/coaches`, icon: Dumbbell, label: t('nav.coaches') });
      items.push({ to: `${base}/students`, icon: GraduationCap, label: t('nav.students', 'Zawodnicy') });
    }
  } else if (activeRole === 'Coach') {
    // Coach Navigation
    const myMemberId = userContext?.activeSchool?.memberId;
    items.push({ to: `${base}/dashboard`, icon: LayoutDashboard, label: t('nav.dashboard', 'Pulpit') });
    items.push({ to: `${base}/coach-reports`, icon: BarChart3, label: t('nav.coachReportsShort', 'Rozliczenia') });
    items.push({ to: `${base}/coach/past-sessions`, icon: History, label: t('nav.coachPastSessionsShort', 'Historia') });
    if (individualSettings?.canManageSlots) {
      items.push({ to: `${base}/coach/individual-classes`, icon: UserRound, label: t('nav.coachIndividualClassesShort') });
    }
    if (enabledModules.includes('tournaments') && userContext?.activeSchool?.enabledFeatures?.includes('Tournaments')) {
      items.push({ to: `${base}/tournaments`, icon: Trophy, label: t('nav.tournaments') });
    }
    if (userContext?.activeSchool?.canManageRacketStringing && userContext.activeSchool.enabledFeatures?.includes('RacketStringing')) {
      items.push({ to: `${base}/racket-stringing`, icon: Wrench, label: t('nav.racketStringingShort') });
    }
    if (myMemberId) {
      items.push({ to: `${base}/coaches/${myMemberId}`, icon: Dumbbell, label: t('nav.myAvailabilityShort', 'Dostępność') });
    }
  } else {
    // Student Navigation: Pulpit, Zajęcia, Lokalizacje
    items.push({ to: `${base}/student`, icon: BookOpen, label: t('nav.studentPortal', 'Pulpit') });
    items.push({ to: `${base}/classes`, icon: Calendar, label: t('nav.classes', 'Zajęcia') });
    items.push({ to: `${base}/locations`, icon: MapPin, label: t('nav.locations', 'Lokalizacje') });
  }

  const isMoreRouteActive = isStudent && ['/payments', '/student/enrollments', '/settings', '/regulations', '/notifications', '/racket-stringing'].some((p) => location.pathname.includes(p));

  return (
    <>
      <nav className="fixed bottom-4 left-4 right-4 z-50 flex justify-between items-center px-1.5 py-2 md:hidden bg-card/90 backdrop-blur-lg border border-muted-foreground/10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)]">
        {items.map((item) => {
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end
              className={({ isActive }) =>
                cn(
                  'flex-1 min-w-0 flex flex-col items-center justify-center transition-all duration-300 relative rounded-xl px-1 py-1.5 hover:scale-105 active:scale-95 cursor-pointer',
                  isActive
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground'
                )
              }
            >
              {({ isActive }) => (
                <>
                  <item.icon
                    className={cn(
                      'size-5 mb-0.5 shrink-0',
                      isActive ? 'stroke-[2.5px]' : ''
                    )}
                  />
                  <span
                    className={cn(
                      'text-[10px] font-bold tracking-tight text-center leading-tight truncate max-w-full block',
                      isActive ? 'text-primary font-extrabold' : 'text-muted-foreground'
                    )}
                    title={item.label}
                  >
                    {item.label}
                  </span>
                </>
              )}
            </NavLink>
          );
        })}

        {/* Student "More" Menu Button */}
        {isStudent && (
          <button
            type="button"
            onClick={() => setMoreMenuOpen(true)}
            className={cn(
              'flex-1 min-w-0 flex flex-col items-center justify-center transition-all duration-300 relative rounded-xl px-1 py-1.5 hover:scale-105 active:scale-95 cursor-pointer border-0 bg-transparent',
              moreMenuOpen || isMoreRouteActive
                ? 'text-primary'
                : 'text-muted-foreground hover:text-foreground'
            )}
          >
            <MoreHorizontal
              className={cn(
                'size-5 mb-0.5 shrink-0',
                moreMenuOpen || isMoreRouteActive ? 'stroke-[2.5px]' : ''
              )}
            />
            <span
              className={cn(
                'text-[10px] font-bold tracking-tight text-center leading-tight truncate max-w-full block',
                moreMenuOpen || isMoreRouteActive ? 'text-primary font-extrabold' : 'text-muted-foreground'
              )}
              title={t('nav.more', 'Więcej')}
            >
              {t('nav.more', 'Więcej')}
            </span>
          </button>
        )}
      </nav>

      {isStudent && (
        <StudentMoreMenuDialog
          open={moreMenuOpen}
          onOpenChange={setMoreMenuOpen}
          schoolId={schoolId}
        />
      )}
    </>
  );
}
